import { useEffect, useState } from 'react';
import { Text, View } from 'react-native';

import { Button } from '@/components/ui/Button';
import { ApkUpdate } from '@/lib/android/apk-update-plugin';
import {
  ANDROID_APK_PATH,
  ANDROID_VERSION_PATH,
  androidReleaseUrl,
  installedAndroidVersionCode,
  runAndroidUpdateCheck,
  type AndroidUpdatePhase,
} from '@/lib/android/release';
import { isNativePlatform } from '@/lib/auth/is-native';
import { getFrontendBaseUrl } from '@/lib/env';
import { nativeAppStorageId } from '@/lib/native-app-id';

type BannerPhase = AndroidUpdatePhase | 'downloading';

function bannerCopy(phase: BannerPhase, detail?: string): string {
  if (phase === 'permission') {
    return 'Allow this app to install updates, then come back.';
  }
  if (phase === 'installing') {
    return 'Confirm the update on the next screen.';
  }
  if (phase === 'error') {
    return detail ? `The update didn't finish. ${detail}` : "The update didn't finish.";
  }
  return 'Downloading the update…';
}

export function AndroidUpdateBanner() {
  const [phase, setPhase] = useState<BannerPhase>('hidden');
  const [detail, setDetail] = useState<string | undefined>();
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!isNativePlatform() || import.meta.env.VITE_ANDROID_UPDATE_CHANNEL !== 'ci') {
      return;
    }

    let cancelled = false;
    let running = false;
    let waitingForPermission = false;
    let openSettings = true;
    const listener: { remove?: () => Promise<void> } = {};

    const check = async () => {
      if (running) {
        return;
      }
      running = true;
      const prompt = openSettings;
      openSettings = false;
      try {
        const frontend = getFrontendBaseUrl();
        const result = await runAndroidUpdateCheck({
          installedVersionCode: installedAndroidVersionCode(import.meta.env.VITE_ANDROID_VERSION_CODE),
          installedApplicationId: nativeAppStorageId(),
          manifestUrl: `${androidReleaseUrl(frontend, ANDROID_VERSION_PATH)}?ts=${Date.now()}`,
          apkUrl: androidReleaseUrl(frontend, ANDROID_APK_PATH),
          fetchManifest: async (url) => {
            const response = await fetch(url, { cache: 'no-store' });
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
            return response.json() as Promise<unknown>;
          },
          downloadAndInstall: (url) => ApkUpdate.downloadAndInstall({ url, prompt }),
          onAvailable: () => {
            if (!cancelled) {
              setDetail(undefined);
              setPhase('downloading');
            }
          },
        });
        if (cancelled) {
          return;
        }
        waitingForPermission = result.phase === 'permission';
        setDetail(result.message);
        setPhase(result.phase);
        if (result.phase === 'error') {
          console.warn('[CS-APK]', result.message);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : 'Update failed';
          console.warn('[CS-APK]', message);
          setDetail(message);
          setPhase('error');
        }
      } finally {
        running = false;
      }
    };

    void (async () => {
      try {
        const handle = await ApkUpdate.addListener('failed', (event) => {
          if (cancelled) {
            return;
          }
          const message = event.message || 'Install failed';
          console.warn('[CS-APK]', message);
          setDetail(message);
          setPhase('error');
        });
        listener.remove = () => handle.remove();
        if (cancelled) {
          await handle.remove();
          return;
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Update failed';
        console.warn('[CS-APK]', message);
      }
      if (!cancelled) {
        await check();
      }
    })();

    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !waitingForPermission) {
        return;
      }
      waitingForPermission = false;
      void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void listener.remove?.();
    };
  }, [retryToken]);

  if (phase === 'hidden') {
    return null;
  }

  const showAction = phase === 'permission' || phase === 'installing' || phase === 'error';
  const actionLabel = phase === 'permission' ? 'Continue' : 'Try again';

  return (
    <View className="shrink-0 gap-2 border-b border-accent bg-accent-muted px-3 py-2">
      <Text className="text-xs text-fg">{bannerCopy(phase, detail)}</Text>
      {showAction ? (
        <Button
          className="self-start"
          label={actionLabel}
          size="sm"
          variant="primary"
          onPress={() => {
            setDetail(undefined);
            setPhase('downloading');
            setRetryToken((token) => token + 1);
          }}
        />
      ) : (
        <Button className="self-start" disabled label="Downloading" loading size="sm" variant="primary" />
      )}
    </View>
  );
}
